import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, User, Clock, CheckCircle, XCircle, AlertCircle, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QuizAttempts = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [quizId]);

  const fetchData = async () => {
    try {
      const [quizRes, attemptsRes] = await Promise.all([
        axios.get(`${API_URL}/quizzes/${quizId}`),
        axios.get(`${API_URL}/quizzes/${quizId}/attempts`)
      ]);
      setQuiz(quizRes.data);
      setAttempts(attemptsRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (attempt) => {
    if (attempt.status === 'calificado') {
      return attempt.aprobado 
        ? <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Aprobado</Badge>
        : <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />No aprobado</Badge>;
    }
    if (attempt.status === 'completado') {
      return <Badge className="bg-blue-100 text-blue-800"><AlertCircle className="w-3 h-3 mr-1" />Pendiente revisión</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />En progreso</Badge>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate statistics
  const completedAttempts = attempts.filter(a => a.status !== 'en_progreso');
  const passedAttempts = attempts.filter(a => a.aprobado);
  const avgScore = completedAttempts.length > 0
    ? (completedAttempts.reduce((sum, a) => sum + a.porcentaje, 0) / completedAttempts.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>
          <div>
            <h1 className="font-manrope font-bold text-2xl text-slate-900">Intentos del Quiz</h1>
            <p className="text-slate-500">{quiz?.titulo}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total intentos</p>
            <p className="text-2xl font-bold text-slate-900">{attempts.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Completados</p>
            <p className="text-2xl font-bold text-slate-900">{completedAttempts.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Aprobados</p>
            <p className="text-2xl font-bold text-green-600">{passedAttempts.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Nota media</p>
            <p className="text-2xl font-bold text-slate-900">{avgScore}%</p>
          </div>
        </div>

        {/* Attempts Table */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-semibold">Listado de intentos</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Alumno</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Puntuación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {attempts.map((attempt) => (
                  <tr key={attempt.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {attempt.student?.nombre} {attempt.student?.apellidos}
                          </p>
                          <p className="text-sm text-slate-500">{attempt.student?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatDate(attempt.fecha_inicio)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-lg font-bold text-slate-900">{attempt.porcentaje}%</div>
                      <div className="text-sm text-slate-500">{attempt.puntuacion} / {attempt.puntuacion_maxima}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(attempt)}
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/quiz-grading/${attempt.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {attempt.status === 'completado' ? 'Calificar' : 'Ver'}
                      </Button>
                    </td>
                  </tr>
                ))}
                {attempts.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No hay intentos registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default QuizAttempts;
